import type { Bloques, Contador, EstadoSuscripcion, Suscripcion } from '../../api/operaciones';
import type { EstadoChip } from '../../componentes/Chip';
import type { NombreIcono } from '../../componentes/iconos';
import type { TarjetaAyuda } from '../../marco/cabecera';
import type { PaqueteWhatsApp } from '../../api/ops/plan';
import type { components } from '../../api/tipos/plataforma';
import { ErrorApi } from '../../api/problema';

/**
 * Lógica de la pantalla del plan (1.15 a 1.17), sin React: textos del estado de la suscripción con las fechas que da
 * `getSubscription`, bloques de `listEntitlements` y contadores de `getUsage`. Textos tal cual del diseño
 * (`1.15-1.17-plan-consumo-pausa-baja.html`); donde el diseño pone una cifra que aún no da la API (precio del catálogo,
 * fecha del cobro fallido) se quita la frase en lugar de inventarla.
 */
export type Dinero = components['schemas']['Money'];
export type Factura = components['schemas']['Invoice'];
export type Bloque = Bloques['entitlements'][number];

export const APARTADOS = ['plan', 'consumo', 'facturas'] as const;
export type Apartado = (typeof APARTADOS)[number];

export function apartadoDe(valor: string | null): Apartado {
    return (APARTADOS as readonly string[]).includes(valor ?? '') ? (valor as Apartado) : 'plan';
}

/** «146,25 €», «49 €»: sin céntimos si son cero (como el diseño). */
export function dinero(m: Dinero): string {
    const euros = m.amount / 100;
    return new Intl.NumberFormat('es-ES', {
        style: 'currency',
        currency: m.currency,
        minimumFractionDigits: Number.isInteger(euros) ? 0 : 2,
        maximumFractionDigits: 2,
        useGrouping: true,
    }).format(euros);
}

/** Fecha de la API (`2026-10-23` o un instante) como «23 de octubre». */
export function dia(fecha: string): string {
    const soloDia = /^(\d{4})-(\d{2})-(\d{2})$/.exec(fecha);
    const d = soloDia === null ? new Date(fecha) : new Date(Number(soloDia[1]), Number(soloDia[2]) - 1, Number(soloDia[3]));
    return d.toLocaleDateString('es-ES', { day: 'numeric', month: 'long' });
}

/** «1 de marzo de 2027»: con el año solo si no es el de `ahora` (como el diseño de 1.17, «Se reactivará sola el…»). */
export function diaConAno(fecha: string, ahora: Date): string {
    const soloDia = /^(\d{4})-(\d{2})-(\d{2})$/.exec(fecha);
    const d = soloDia === null ? new Date(fecha) : new Date(Number(soloDia[1]), Number(soloDia[2]) - 1, Number(soloDia[3]));
    return d.getFullYear() === ahora.getFullYear() ? dia(fecha) : d.toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' });
}

/** Días antes de la vuelta en que se avisa al propietario (G1034: la campana y «Vuelve en 7 días» de 1.17). */
export const AVISO_VUELTA_DIAS = 7;

/** La misma fecha, `n` días después, como «1 de diciembre» (lo que pasa después de la baja, G198, G204, G242). */
export function diaMasDias(fecha: string, n: number): string {
    const soloDia = /^(\d{4})-(\d{2})-(\d{2})$/.exec(fecha);
    const d = soloDia === null ? new Date(fecha) : new Date(Number(soloDia[1]), Number(soloDia[2]) - 1, Number(soloDia[3]));
    d.setDate(d.getDate() + n);
    return d.toLocaleDateString('es-ES', { day: 'numeric', month: 'long' });
}

/** «01/10/2026», para la tabla de facturas. */
export function diaCorto(fecha: string): string {
    const soloDia = /^(\d{4})-(\d{2})-(\d{2})$/.exec(fecha);
    return soloDia === null ? new Date(fecha).toLocaleDateString('es-ES') : `${soloDia[3] ?? ''}/${soloDia[2] ?? ''}/${soloDia[1] ?? ''}`;
}

/** Días que quedan hasta una fecha (redondeando hacia arriba; nunca negativos). */
export function diasHasta(fecha: string, ahora: Date): number {
    return Math.max(0, Math.ceil((new Date(fecha).getTime() - ahora.getTime()) / 86_400_000));
}

/** «1.000», «2.000» (el diseño agrupa también los de cuatro cifras). */
export function miles(n: number): string {
    return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

/** «Vuelves con» de la pausa a punto de acabar (1.17): «146,25 € al mes» y debajo «Base y 6 bloques» (G1190). */
function vueltaCon(cuota: Dinero | null | undefined, baseYBloques: string): Dato {
    return cuota == null ? { clave: 'Vuelves con', valor: baseYBloques } : { clave: 'Vuelves con', valor: `${dinero(cuota)} al mes`, sub: baseYBloques };
}

export interface Dato {
    clave: string;
    valor: string;
    sub?: string;
    rojo?: boolean;
}

/** Acción de la tarjeta del plan (solo con `plan.contratar` o `plan.pausar` y con el cobro disponible). */
export type AccionPlan = 'contratar' | 'metodo' | 'pagar' | 'reactivar' | 'anular-baja' | 'cancelar-pausa';

export interface TarjetaPlan {
    chip: { texto: string; estado: EstadoChip } | null;
    titulo: string;
    texto: string;
    datos: Dato[];
    acciones: { accion: AccionPlan; texto: string; icono: NombreIcono; secundaria?: boolean }[];
    /** «⋯»: cambiar el método de pago, pausar y dar de baja (lo poco frecuente y lo que asusta, 1.17). */
    menu: ('metodo' | 'pausar' | 'baja')[];
}

const ETIQUETAS: Record<EstadoSuscripcion, [string, EstadoChip] | null> = {
    trialing: ['En prueba', 'neutral'],
    active: null,
    past_due: ['Impagada', 'problem'],
    suspended: ['Suspendida', 'problem'],
    paused: ['Pausada', 'neutral'],
    cancel_scheduled: ['Baja programada', 'pending'],
    cancelled: ['Cancelada', 'problem'],
    trial_expired: ['Prueba caducada', 'problem'],
    closed: ['Cerrada', 'problem'],
};

function metodo(s: Suscripcion, rechazado = false): Dato | null {
    const m = s.payment_method;
    if (m === null || m === undefined) {
        return null;
    }
    const marca = m.brand === null || m.brand === undefined || m.brand === '' ? (m.type === 'sepa_debit' ? 'Cuenta' : 'Tarjeta') : m.brand.charAt(0).toUpperCase() + m.brand.slice(1);
    return {
        clave: 'Método de pago',
        valor: `${marca} •••• ${m.last4 ?? ''}`.trim(),
        sub: rechazado ? 'Cobro rechazado' : m.type === 'sepa_debit' ? 'Domiciliación SEPA' : 'Tarjeta',
        rojo: rechazado,
    };
}

function sinNulos<T>(lista: readonly (T | null)[]): T[] {
    return lista.filter((x): x is T => x !== null);
}

/** Bloques de pago (los de la suscripción o la prueba, no los incluidos sin coste) para «Base y N bloques». */
export function bloquesDePago(bloques: readonly Bloque[]): Bloque[] {
    return bloques.filter((b) => b.product_code !== 'base' && (b.source === 'subscription' || b.source === 'trial'));
}

/**
 * La tarjeta de arriba del apartado Plan para cada estado (los textos del diseño con las fechas de la API). `ahora` para
 * los días de prueba que quedan.
 */
export function tarjetaPlan(s: Suscripcion, bloques: readonly Bloque[], ahora: Date): TarjetaPlan {
    const etiqueta = ETIQUETAS[s.status];
    const chip = etiqueta === null ? null : { texto: etiqueta[0], estado: etiqueta[1] };
    const cobro = s.next_charge?.amount === undefined ? null : dinero(s.next_charge.amount);
    const n = bloquesDePago(bloques).length;
    const baseYBloques = n === 0 ? 'Base' : `Base y ${String(n)} ${n === 1 ? 'bloque' : 'bloques'}`;
    const base = { chip, datos: [] as Dato[], acciones: [] as TarjetaPlan['acciones'], menu: [] as TarjetaPlan['menu'] };

    switch (s.status) {
        case 'trialing': {
            const dias = s.trial_ends_at === null || s.trial_ends_at === undefined ? null : diasHasta(s.trial_ends_at, ahora);
            return {
                ...base,
                titulo: dias === null ? 'Estás en la prueba' : dias <= 1 ? 'Te queda 1 día de prueba' : `Te quedan ${String(dias)} días de prueba`,
                // G997, G998: sin tarjeta, la base y todos los bloques con cupos reducidos (los de la prueba).
                texto: 'La prueba dura 14 días e incluye la base y todos los bloques, con cupos reducidos. Al acabar, el panel queda en solo lectura hasta que contrates. Sin permanencia.',
                datos: sinNulos([s.trial_ends_at === null || s.trial_ends_at === undefined ? null : { clave: 'Prueba hasta', valor: dia(s.trial_ends_at) }]),
                acciones: [{ accion: 'contratar', texto: 'Contratar ahora', icono: 'credit-card' }],
            };
        }
        case 'active': {
            // G1034: la pausa programada sigue activa y vendiendo hasta su día de inicio (C.52, `?suscripcion=programada`).
            // «Cancelar la pausa» la anula sin coste (`resumeSubscription`, G1176, ADR-0268); sin baja mientras está programada (G1072).
            const inicio = s.pause?.starts_on;
            if (inicio !== undefined) {
                const vuelta = s.pause?.expected_return_on;
                return {
                    ...base,
                    chip: { texto: 'Pausa programada', estado: 'pending' },
                    titulo: `La pausa empieza el ${diaConAno(inicio, ahora)}`,
                    texto: `Hasta entonces todo funciona igual y sigues vendiendo. Desde ese día, 9 € al mes y el panel en solo lectura.${vuelta === undefined ? '' : ` Se reactivará sola el ${diaConAno(vuelta, ahora)}.`}`,
                    datos: sinNulos([
                        { clave: 'Empieza el', valor: diaConAno(inicio, ahora), sub: 'La cuota de pausa, desde el siguiente cobro' },
                        vuelta === undefined ? null : { clave: 'Se reactivará sola el', valor: diaConAno(vuelta, ahora), sub: 'Puedes reactivar antes' },
                        metodo(s),
                    ]),
                    acciones: [{ accion: 'cancelar-pausa', texto: 'Cancelar la pausa', icono: 'x', secundaria: true }],
                    menu: ['metodo'],
                };
            }
            return {
                ...base,
                titulo: cobro === null ? 'Tu plan está activo' : `${cobro} al mes`,
                texto: `${baseYBloques}. Sin permanencia.`,
                datos: sinNulos([
                    s.next_charge?.amount === undefined ? null : { clave: 'Próximo cobro', valor: dinero(s.next_charge.amount), sub: s.next_charge.date === undefined ? undefined : `El ${dia(s.next_charge.date)}` },
                    metodo(s),
                    s.coupon_code === null || s.coupon_code === undefined ? null : { clave: 'Descuento', valor: s.coupon_code },
                ]),
                menu: ['metodo', 'pausar', 'baja'],
            };
        }
        case 'past_due': {
            const plazo = s.grace_ends_at === null || s.grace_ends_at === undefined ? null : dia(s.grace_ends_at);
            return {
                ...base,
                titulo: cobro === null ? 'No hemos podido cobrar la cuota' : `No hemos podido cobrar ${cobro}`,
                texto:
                    plazo === null
                        ? 'Tienes 14 días de gracia: todo sigue funcionando y, si no se paga, el panel queda en solo lectura.'
                        : `Tienes 14 días de gracia: todo sigue funcionando hasta el ${plazo} y, si no se paga, el panel queda en solo lectura.`,
                datos: sinNulos([
                    cobro === null ? null : { clave: 'Pendiente', valor: cobro },
                    plazo === null ? null : { clave: 'Plazo para pagar', valor: plazo, sub: 'Después, solo lectura' },
                    metodo(s, true),
                ]),
                acciones: [{ accion: 'metodo', texto: 'Actualizar el método de pago', icono: 'credit-card' }],
            };
        }
        case 'suspended':
            return {
                ...base,
                titulo: 'Tu plan está suspendido',
                texto: 'Acabó el periodo de gracia sin pago. El panel está en solo lectura: puedes consultar y exportar. Al pagar, vuelve todo como estaba.',
                datos: sinNulos([cobro === null ? null : { clave: 'Pendiente', valor: cobro }, metodo(s, true)]),
                acciones: [{ accion: 'pagar', texto: cobro === null ? 'Pagar' : `Pagar · ${cobro}`, icono: 'credit-card' }],
            };
        case 'paused': {
            // G1034: ese día se reactiva sola (con aviso 7 días antes, el de la campana); «Reactivar» vuelve antes.
            const vuelta = s.pause?.expected_return_on;
            const quedan = vuelta === undefined ? null : diasHasta(vuelta, ahora);
            if (vuelta !== undefined && quedan !== null && quedan <= AVISO_VUELTA_DIAS) {
                return {
                    ...base,
                    // G1192: cuenta los días que quedan («Vuelve en 5 días») y con uno dice «Vuelve mañana».
                    chip: { texto: quedan <= 1 ? 'Vuelve mañana' : `Vuelve en ${String(quedan)} días`, estado: 'pending' },
                    titulo: `Tu pausa acaba el ${diaConAno(vuelta, ahora)}`,
                    texto: 'Ese día tu negocio se reactiva solo y vuelve la cuota de tu plan, con los mismos bloques. Si quieres volver antes, pulsa Reactivar.',
                    datos: sinNulos([
                        { clave: 'Se reactivará sola el', valor: diaConAno(vuelta, ahora), sub: 'Con la parte proporcional del mes' },
                        // G1190: la cuota con los bloques guardados (`pause.return_monthly_fee`); sin ella, solo la base y los bloques.
                        vueltaCon(s.pause?.return_monthly_fee, baseYBloques),
                        metodo(s),
                    ]),
                    acciones: [{ accion: 'reactivar', texto: 'Reactivar', icono: 'play' }],
                    menu: ['metodo'],
                };
            }
            return {
                ...base,
                titulo: 'Pausa de temporada · 9 € al mes',
                texto: `Tu configuración, el plano y el CRM se conservan. El panel está en solo lectura: no entran reservas ni ventas.${vuelta === undefined ? '' : ` Se reactivará sola el ${diaConAno(vuelta, ahora)}.`}`,
                datos: sinNulos([
                    s.next_charge?.amount === undefined ? null : { clave: 'Próximo cobro', valor: dinero(s.next_charge.amount), sub: s.next_charge.date === undefined ? undefined : `El ${dia(s.next_charge.date)}` },
                    vuelta === undefined ? null : { clave: 'Se reactivará sola el', valor: diaConAno(vuelta, ahora), sub: 'Puedes reactivar antes' },
                    metodo(s),
                ]),
                acciones: [{ accion: 'reactivar', texto: 'Reactivar', icono: 'play' }],
                menu: ['metodo'],
            };
        }
        case 'cancel_scheduled': {
            const fin = s.cancel_at === null || s.cancel_at === undefined ? null : dia(s.cancel_at);
            return {
                ...base,
                titulo: fin === null ? 'Baja programada' : `Se da de baja el ${fin}`,
                texto: 'Hasta entonces todo funciona igual. Antes puedes descargar tus datos o anular la baja.',
                datos: sinNulos([metodo(s)]),
                acciones: [{ accion: 'anular-baja', texto: 'Anular la baja', icono: 'rotate-ccw' }],
                menu: ['metodo'],
            };
        }
        case 'cancelled':
            return {
                ...base,
                titulo: 'Tu plan está cancelado',
                texto: 'El panel está en solo lectura. Tus datos siguen aquí: puedes consultarlos, exportarlos o volver a contratar.',
                acciones: [{ accion: 'contratar', texto: 'Volver a contratar', icono: 'credit-card' }],
            };
        case 'trial_expired':
            return {
                ...base,
                titulo: 'Tu prueba ha caducado',
                texto: 'Tus datos siguen aquí. El panel está en solo lectura: contrata para volver a recibir reservas y ventas.',
                acciones: [{ accion: 'contratar', texto: 'Contratar', icono: 'credit-card' }],
            };
        case 'closed':
            return { ...base, titulo: 'Este negocio está cerrado', texto: 'El panel está en solo lectura.' };
    }
}

/**
 * Nombre de un bloque. Los nombres, las descripciones y los precios son del catálogo (`getCatalog`, P0-7), que aún no
 * está: hasta entonces se escribe el código del bloque legible («escaner_tablet» → «Escaner tablet»), sin inventarlo.
 */
export function nombreBloque(codigo: string): string {
    const texto = codigo.replace(/_/g, ' ');
    return texto.charAt(0).toUpperCase() + texto.slice(1);
}

const ORIGEN: Record<Bloque['source'], string> = {
    subscription: 'Contratado',
    included: 'Incluido sin coste',
    trial: 'En la prueba',
    platform_grant: 'Regalo de Reservaria',
    migration: 'De la versión anterior',
};

export function origenBloque(b: Bloque): string {
    // G170: Reservas y Citas son bloques sin coste que se eligen en Negocio › Qué gestiona.
    return b.product_code === 'reservas' || b.product_code === 'citas' ? 'Sin coste · se elige en Negocio' : ORIGEN[b.source];
}

/** La base primero; después por código. */
export function ordenarBloques(bloques: readonly Bloque[]): Bloque[] {
    return [...bloques].sort((a, b) => (a.product_code === 'base' ? -1 : b.product_code === 'base' ? 1 : a.product_code.localeCompare(b.product_code)));
}

// ---- Consumo (1.16) ----

const CONCEPTOS: Record<Contador['concept'], { etiqueta: string; unidad: string; icono: NombreIcono; prepago: boolean }> = {
    conversations: { etiqueta: 'Conversaciones del asistente', unidad: 'conversaciones', icono: 'message-circle', prepago: false },
    sends: { etiqueta: 'Envíos de campañas', unidad: 'envíos', icono: 'send', prepago: false },
    voice_minutes: { etiqueta: 'Minutos de voz', unidad: 'minutos', icono: 'phone', prepago: false },
    signatures: { etiqueta: 'Firmas', unidad: 'firmas', icono: 'signature', prepago: false },
    professionals: { etiqueta: 'Profesionales', unidad: 'profesionales', icono: 'users', prepago: false },
    // G525, G551, G552, G703: todo mensaje de WhatsApp enviado consume. «Mensajes normales» (respuestas, confirmaciones y
    // avisos: lo incluido en el plan y después los paquetes Servicio y Chatbot) y «Mensajes de marketing» (su paquete).
    // G711, G712: «Paquete normal» agrupa lo incluido, Servicio y Chatbot; «Marketing», lo incluido de marketing y su paquete.
    whatsapp: { etiqueta: 'Paquete normal · incluido en el plan', unidad: 'mensajes enviados', icono: 'message-circle', prepago: false },
    whatsapp_transactional: { etiqueta: 'Paquete normal · Servicio', unidad: 'mensajes enviados', icono: 'wallet', prepago: true },
    whatsapp_chatbot: { etiqueta: 'Paquete normal · Chatbot', unidad: 'mensajes enviados', icono: 'bot', prepago: true },
    whatsapp_marketing_plan: { etiqueta: 'Marketing · incluido en el plan', unidad: 'mensajes enviados', icono: 'megaphone', prepago: false },
    whatsapp_marketing: { etiqueta: 'Marketing · paquete extra', unidad: 'mensajes enviados', icono: 'megaphone', prepago: true },
};

export interface Medidor {
    clave: string;
    etiqueta: string;
    icono: NombreIcono;
    /** null sin cifra de lo incluido (G560, G711): se cuenta y no se corta, sin arco. */
    porcentaje: number | null;
    /** «900 de 1.000 conversaciones». */
    deCuanto: string;
    /** «Al 90 %: quedan 100», «Quedan 580 · no caducan con el mes». */
    nota: string;
    alerta: boolean;
    /** Debajo del arco (WhatsApp, G703, G711, G712): qué cuenta y lo que queda después en el paquete. */
    partes?: { texto: string; valor: string }[];
}

/** WhatsApp va en sus dos tarjetas («Mensajes normales» y «Mensajes de marketing», `tarjetasWhatsApp`), no en un arco por concepto. */
const DE_WHATSAPP: readonly Contador['concept'][] = ['whatsapp', 'whatsapp_transactional', 'whatsapp_chatbot', 'whatsapp_marketing_plan', 'whatsapp_marketing'];

/** Contadores de getUsage como los arcos de 1.16: los que están al 90 % o más, primero. Sin cupo, no hay arco. */
export function medidores(contadores: readonly Contador[], paquetes: readonly PaqueteWhatsApp[] | null = null): Medidor[] {
    const deWhatsApp = tarjetasWhatsApp(contadores, paquetes);
    return contadores
        .filter((c) => !DE_WHATSAPP.includes(c.concept))
        .map((c): Medidor | null => {
            const def = CONCEPTOS[c.concept];
            const total = c.included + c.extra;
            if (total <= 0) {
                return null;
            }
            const queda = def.prepago && c.balance !== null && c.balance !== undefined ? c.balance : Math.max(0, total - c.used);
            const porcentaje = Math.min(100, Math.round((c.used / total) * 100));
            const alerta = porcentaje >= 90;
            return {
                clave: c.concept,
                etiqueta: def.etiqueta,
                icono: def.icono,
                porcentaje,
                deCuanto: `${miles(c.used)} de ${miles(total)} ${def.unidad}`,
                nota: alerta ? `Al 90 %: quedan ${miles(queda)}` : def.prepago ? `Quedan ${miles(queda)} · no caducan con el mes` : `Quedan ${miles(queda)}`,
                alerta,
            };
        })
        .filter((m): m is Medidor => m !== null)
        .concat(deWhatsApp)
        .sort((a, b) => Number(b.alerta) - Number(a.alerta));
}

/** Nombres de los paquetes de WhatsApp (G712: Servicio y Chatbot forman el «paquete normal»), para «Comprar más». */
export const NOMBRE_PAQUETE: Readonly<Record<PaqueteWhatsApp['pack'], string>> = {
    transactional: 'Paquete normal · Servicio',
    chatbot: 'Paquete normal · Chatbot',
    marketing: 'Paquete Marketing',
};

/**
 * Las dos tarjetas de WhatsApp de 1.16 (G703, G711, G712): el arco es lo incluido en el plan del periodo (`whatsapp` y
 * `whatsapp_marketing_plan` de getUsage); debajo, lo que queda después en el paquete (`listWhatsAppPacks`: normal =
 * Servicio + Chatbot; Marketing). Sin cifra de lo incluido (G560), la tarjeta cuenta lo enviado, sin arco ni corte.
 */
export function tarjetasWhatsApp(contadores: readonly Contador[], paquetes: readonly PaqueteWhatsApp[] | null): Medidor[] {
    const queda = (codigos: readonly PaqueteWhatsApp['pack'][]): string => {
        if (paquetes === null) {
            return '';
        }
        return `Quedan ${miles(paquetes.filter((p) => codigos.includes(p.pack)).reduce((n, p) => n + p.messages_available, 0))}`;
    };
    const tarjeta = (c: Contador | undefined, clave: string, etiqueta: string, icono: NombreIcono, partes: { texto: string; valor: string }[]): Medidor | null => {
        if (c === undefined) {
            return null;
        }
        const total = c.included + c.extra;
        if (total <= 0) {
            return { clave, etiqueta, icono, porcentaje: null, deCuanto: `${miles(c.used)} enviados`, nota: '', alerta: false, partes };
        }
        const porcentaje = Math.min(100, Math.round((c.used / total) * 100));
        const resto = miles(Math.max(0, total - c.used));
        return {
            clave,
            etiqueta,
            icono,
            porcentaje,
            deCuanto: `${miles(c.used)} de ${miles(total)} enviados de los incluidos en tu plan`,
            nota: porcentaje >= 90 ? `Al 90 %: quedan ${resto}` : `Quedan ${resto}`,
            alerta: porcentaje >= 90,
            partes,
        };
    };
    return [
        tarjeta(contadores.find((c) => c.concept === 'whatsapp'), 'whatsapp', 'Mensajes normales', 'message-circle', [
            { texto: 'Respuestas, confirmaciones y avisos por WhatsApp', valor: '' },
            { texto: 'Después, tu paquete normal (Servicio y Chatbot)', valor: queda(['transactional', 'chatbot']) },
        ]),
        tarjeta(contadores.find((c) => c.concept === 'whatsapp_marketing_plan'), 'whatsapp_marketing_plan', 'Mensajes de marketing', 'megaphone', [
            { texto: 'Campañas por WhatsApp', valor: '' },
            { texto: 'Después, tu paquete Marketing', valor: queda(['marketing']) },
        ]),
    ].filter((m): m is Medidor => m !== null);
}

/**
 * «Del 1 al 31 de octubre»: el periodo de los contadores (el del primero). En la prueba (`trial_ends_at`), «Prueba del 9 al
 * 23 de octubre · cupos de la prueba» (G998, G1036): desde el inicio del periodo hasta el fin de la prueba.
 */
export function periodo(contadores: readonly Contador[], finDeLaPrueba: string | null = null): string | null {
    const c = contadores.find((x) => !CONCEPTOS[x.concept].prepago) ?? contadores[0];
    if (c === undefined) {
        return null;
    }
    if (finDeLaPrueba === null) {
        return `Del ${dia(c.period_start)} al ${dia(c.period_end)}`;
    }
    // «del 9 al 23 de octubre»: el mes una sola vez si es el mismo.
    const desde = dia(c.period_start);
    const hasta = dia(finDeLaPrueba);
    const mes = (t: string) => t.replace(/^\d+ /, '');
    return `Prueba del ${mes(desde) === mes(hasta) ? desde.replace(/ de .*$/, '') : desde} al ${hasta} · cupos de la prueba`;
}

/** Debajo del periodo en la prueba (G1036): lo que no tiene cupo de prueba se cuenta sin cortar. */
export const NOTA_PRUEBA_CONSUMO = 'Durante la prueba, los bloques sin cupo de prueba, como los mensajes de WhatsApp, se cuentan sin cortar.';

// ---- Facturas ----

export const ESTADO_FACTURA: Record<Factura['status'], [string, EstadoChip] | null> = {
    paid: null,
    open: ['Pendiente', 'pending'],
    uncollectible: ['Fallida', 'problem'],
    void: ['Anulada', 'neutral'],
};

// ---- «?» de cada apartado (textos del diseño) ----

export const AYUDA: Record<Apartado, TarjetaAyuda[]> = {
    plan: [
        { icono: 'layers', titulo: 'Base y bloques', texto: 'La base incluye Reservas y Citas. Lo demás son bloques: contratas solo lo que usas.' },
        { icono: 'pause', titulo: 'Pausa de temporada', texto: 'Por 9 € al mes se guarda todo y el panel queda en solo lectura hasta que vuelvas.' },
        { icono: 'calendar-x', titulo: 'Sin permanencia', texto: 'La baja es efectiva al final del periodo pagado. Antes puedes descargar todos tus datos.' },
    ],
    consumo: [
        { icono: 'gauge', titulo: 'Cupos del mes', texto: 'Te avisamos en la campana al 90 % y el arco se pone rojo. Se renuevan el 1 de cada mes; los paquetes de WhatsApp no caducan.' },
        {
            icono: 'circle-alert',
            titulo: 'Al llegar al 100 %',
            // G713: lo que pasa al agotarse el normal y el de marketing (texto del diseño, 29-oct).
            texto: 'Sin mensajes normales, las confirmaciones salen por email, el asistente deja de responder por WhatsApp y el buzón no envía por WhatsApp. Sin mensajes de marketing, las campañas por WhatsApp se pausan.',
        },
        { icono: 'message-circle', titulo: 'Qué cuenta', texto: 'Solo cuenta como conversación cuando el cliente habla con el asistente. Enviar una entrada o un recordatorio no cuenta.' },
        {
            icono: 'wallet',
            titulo: 'Mensajes de WhatsApp',
            // G703: todo mensaje enviado gasta uno, también las respuestas.
            texto: 'Cada mensaje de WhatsApp enviado gasta uno, también las respuestas. Primero los incluidos en tu plan y después tu paquete: el normal para respuestas, confirmaciones y avisos; el de marketing para campañas. Los paquetes no caducan.',
        },
    ],
    facturas: [
        { icono: 'receipt', titulo: 'Facturas', texto: 'Una al mes por la suscripción y otra por cada paquete que compres.' },
        { icono: 'credit-card', titulo: 'Si falla un cobro', texto: 'Lo reintentamos y te avisamos en la campana.' },
    ],
};

type ProblemaCompromisos = components['schemas']['CommitmentsProblem'];

/** Cómo se llama cada tipo de compromiso en la ventana «Todavía no puedes pausar» (1.17). */
const QUE: Record<string, [string, string]> = {
    reservations: ['reserva', 'reservas'],
    appointments: ['cita', 'citas'],
    tickets: ['entrada', 'entradas'],
    scheduled_campaigns: ['campaña programada', 'campañas programadas'],
};

/**
 * Lo que impide pausar (`409 commitments_pending`, G1002, G1034): una línea por tipo («3 reservas · hasta el 14 de marzo»,
 * como el diseño) y el primer día en que puede empezar la pausa (`earliest_starts_on`). Null si el error es otro.
 */
export function compromisosDePausa(e: unknown): { desde: string | null; lineas: { clave: string; valor: string }[] } | null {
    if (!(e instanceof ErrorApi) || e.codigo !== 'commitments_pending') {
        return null;
    }
    const p = e.problema as unknown as ProblemaCompromisos;
    return {
        desde: p.earliest_starts_on ?? null,
        lineas: (p.commitments ?? []).map((c) => {
            const [uno, varios] = QUE[c.kind] ?? ['compromiso', 'compromisos'];
            return {
                clave: `${new Intl.NumberFormat('es-ES').format(c.count)} ${c.count === 1 ? uno : varios}`,
                valor: c.last_date === null || c.last_date === undefined ? '' : `hasta el ${dia(c.last_date)}`,
            };
        }),
    };
}
